import { useState, useContext, useRef, useEffect } from "react";
import { Autocomplete, Box, Grid } from "@mui/material";
import TitleBar from "../../components/layout-components/TitleBar";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import "./AddTasks.scss";
import CustomizedDatePicker from "../../components/CustomizedDatePicker";
import { useTranslation } from "react-i18next";
import CustomTextField from "../../components/DescriptionBox";
import AppContext from "../../config/AppContext";

import CancelDialog from "./CancelDialog";
import CustomButton from "../../components/CustomButton";
import { useLocation, useNavigate } from "react-router-dom";
import {
  findProjectsByAssignedMember,
  getAllHRManagerProjects,
  listProject,
} from "../../services/ProjectService";
import { toast } from "sonner";
import CommonUtil from "../../util/CommonUtils";
import { createTask } from "../../services/TaskService";
import CustomProjectSelect from "../../components/CustomProjectAutoComplete";
import moment from "moment/moment";

export default function AddTasks() {
  const { t, i18n } = useTranslation();
  const [title, setTitle] = useState("");
  const [taskDescription, setTaskDescription] = useState("");
  const [projectId, setProjectId] = useState("");
  const [timelineStartDate, setTimelineStartDate] = useState(null);
  const [timelineEndDate, setTimelineEndDate] = useState(null);
  const [isSaveButtonDisable, setSaveButtonDisable] = useState(false);
  const navigate = useNavigate();
  const [selectedProject, setSelectedProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const location = useLocation();
  const fromView = location.state ? location.state?.fromView : null;

  const [error, setError] = useState(null);

  const { mode } = useContext(AppContext);

  const [expandedAccordion, setExpandedAccordion] = useState("basic");
  const roleCode = CommonUtil.getRoleCode();

  const [state, setState] = useState({
    title: "",
    description: "",
    projectId: "",
    startDate: "",
    endDate: "",
    dueDate: "",
  });

  const [errors, setErrors] = useState({
    title: "",
    description: "",
    projectId: "",
    startDate: "",
    endDate: "",
  });

  const projectStartDate = selectedProject
    ? new Date(selectedProject.startDate)
    : null;
  const projectEndDate = selectedProject
    ? new Date(selectedProject.endDate)
    : null;

  const handleProjectSet = (value) => {
    const project = (projects || []).find((p) => p.id === value);
    setProjectId(value);
    setSelectedProject(project || null);
    const previousProjectId = projectId;

    if (!value || value !== previousProjectId) {
      setTimelineStartDate(null);
      setTimelineEndDate(null);
      setErrors((prevErrors) => ({
        ...prevErrors,
        timelineStartDate: "",
        timelineEndDate: "",
      }));
    }
    if (value && value === previousProjectId && project) {
      const projectStart = project ? new Date(project.startDate) : null;
      const projectEnd = project ? new Date(project.endDate) : null;

      let validStartDate = timelineStartDate;
      let validEndDate = timelineEndDate;

      if (
        timelineStartDate &&
        (timelineStartDate < projectStart || timelineStartDate > projectEnd)
      ) {
        validStartDate = null;
      }

      if (
        timelineEndDate &&
        (timelineEndDate < projectStart || timelineEndDate > projectEnd)
      ) {
        validEndDate = null;
      }

      setTimelineStartDate(validStartDate);
      setTimelineEndDate(validEndDate);
    }

    setErrors({
      ...errors,
      projectId: "",
      timelineStartDate: "",
      timelineEndDate: "",
    });
  };

  const handleCancelButton = () => {
    const isPayloadEmpty = !(
      title !== "" ||
      state.description !== "" ||
      timelineStartDate !== null ||
      timelineEndDate !== null ||
      projectId !== ""
    );

    if (!isPayloadEmpty) {
      setIsCancelDialogOpen(true);
    } else {
      window.history.go(-1);
    }
  };

  const handleCloseCancelDialog = () => {
    setIsCancelDialogOpen(false);
  };

  const handleConfirmCancel = () => {
    setIsCancelDialogOpen(false);
    navigate("/tasks");
  };

  const handleDescriptionChange = (e) => {
    let inputValue = e.target.value;
    let chars = Array.from(inputValue);

    if (chars.length > 2000) {
      inputValue = chars.slice(0, 2000).join('');
    }

    setState((prevState) => ({
      ...prevState,
      description: inputValue,
    }));

    setErrors((prevErrors) => ({
      ...prevErrors,
      description: "",
    }));
  };

  useEffect(() => {
    if (textAreaRef.current) {
      textAreaRef.current.style.height = "auto";
      const { scrollHeight } = textAreaRef.current;
      const height = Math.max(40, Math.min(scrollHeight, 300));
      textAreaRef.current.style.height = `${height}px`;
    }
  }, [state.description]);

  const textAreaRef = useRef(null);

  // useEffect(() => {
  //     rolesList().then((res) => {
  //         if (res?.data?.code === "UASI0000") {
  //             const clientRole = res?.data?.data?.roles?.find(
  //                 (role) => role?.name === "Client"
  //             );
  //             if (clientRole) {
  //                 clientList({ roleId: clientRole?.id }).then((r) => {
  //                     if (r?.data?.code === "RCI0000") {
  //                         const updatedClients = r?.data?.data
  //                             ?.filter((user) => user?.status !== "ARCHIVED")
  //                             .map((user, index) => {
  //                                 return {
  //                                     id: user?.userId,
  //                                     name: user?.name,
  //                                 };
  //                             })
  //                             ?.sort((a, b) => a.name.localeCompare(b.name));
  //                         setClients((prevState) => [...prevState, ...updatedClients]);
  //                     }
  //                 });
  //             } else {
  //                 console.log("Client role not found");
  //             }
  //         }
  //     });
  // }, []);

  const validateFields = () => {
    let valid = true;
    const newErrors = { ...errors };

    Object.keys(newErrors).forEach((key) => {
      newErrors[key] = "";
    });

    if (!title.trim()) {
      newErrors.title = t("RAL0001");
      valid = false;
    } else if (title.length < 1) {
      newErrors.title = t("RAL1133");
      valid = false;
    } else if (title.length > 255) {
      newErrors.title = t("RAL1134");
      valid = false;
    }

    if (state.description && state.description.length > 2000) {
      newErrors.description = t("RAL0550");
      valid = false;
    }

    if (!timelineStartDate && selectedProject != null) {
      newErrors.timelineStartDate = t("RAL0551");
      valid = false;
    }

    if (
      !timelineEndDate &&
      projectEndDate != null &&
      moment(projectEndDate).isValid()
    ) {
      newErrors.timelineEndDate = t("RAL0552");
      valid = false;
    }

    if (
      timelineStartDate &&
      timelineEndDate &&
      timelineStartDate > timelineEndDate &&
      projectEndDate != null
    ) {
      newErrors.timelineStartDate = t("RAL0553");
      newErrors.timelineEndDate = t("RAL0554");
      valid = false;
    }

    if (!projectId.trim()) {
      newErrors.projectId = t("RAL0001");
      valid = false;
    }

    setErrors(newErrors);

    return valid;
  };

  const handleCreateTask = () => {
    if (validateFields()) {
      const payload = {
        title: title.trim(),
        description: state.description.trim(),
        startDate: timelineStartDate,
        endDate: timelineEndDate,
        projectId: projectId,
        dueDate: timelineEndDate,
      };
      setSaveButtonDisable(true);
      createTask(payload)
        .then((res) => {
           setSaveButtonDisable(false);
          if (res.data.code === "RCI0051") {
            toast.success(t("RAL1107"));
            if (location.state?.backto == undefined) {
              navigate("/tasks");
            } else {
              navigate(location.state?.backto);
            }
            setErrors({});
          } else {
            toast.error(res.data.message);
          }
        })
        .catch((error) => {
          console.error("Error creating task:", error);
          toast.error(t("RAL0326"));
        });
    }
  };

  useEffect(() => {
    setErrors({});
  }, [i18n.language]);

  const handleTimelineStartDate = (date) => {
    if (date === null || date === "" || (date && isNaN(date.valueOf()))) {
      setTimelineStartDate(null);
      setErrors((prevErrors) => ({
        ...prevErrors,
        timelineStartDate: t("RAL0551"),
      }));
    } else {
      setTimelineStartDate(date);
      if (timelineEndDate && date && date > timelineEndDate) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          timelineStartDate: t("RAL0553"),
          timelineEndDate: t("RAL0554"),
        }));
      } else {
        setErrors((prevErrors) => ({
          ...prevErrors,
          timelineStartDate: "",
          timelineEndDate: "",
        }));
      }
    }
  };

  const handleTimelineEndDate = (date) => {
    if (date === null || date === "" || (date && isNaN(date.valueOf()))) {
      setTimelineEndDate(null);
      if (
        projectStartDate &&
        projectEndDate &&
        moment(projectEndDate).isValid()
      ) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          timelineEndDate: t("RAL0552"),
        }));
      }
      if (date === null) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          timelineStartDate: "",
          timelineEndDate: "",
        }));
      }
    } else {
      setTimelineEndDate(date);
      if (timelineStartDate && date && timelineStartDate > date) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          timelineStartDate: t("RAL0553"),
          timelineEndDate: t("RAL0554"),
        }));
      } else {
        setErrors((prevErrors) => ({
          ...prevErrors,
          timelineStartDate: "",
          timelineEndDate: "",
        }));
      }
    }
  };

  useEffect(() => {
  const loadProjectsBasedOnRole = async () => {
    try {
      if (roleCode === "HR_MANAGER") {
        const res = await getAllHRManagerProjects();
        if (res?.data?.code === "RCI0000" && Array.isArray(res?.data?.data)) {
          setProjects(res.data.data);
        } else {
          setProjects([]);
        }
      } else {
        const res = await listProject();
        if (
          res?.data?.code === "RCI0000" &&
          Array.isArray(res.data.data.projects)
        ) {
          const filteredProjects = res.data.data.projects.filter(
            (project) =>
              project &&
              project.status !== "ARCHIVED" &&
              project.status !== "COMPLETED"
          );
          setProjects(filteredProjects);
        } else {
          setProjects([]);
        }
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
      setProjects([]);
    }
  };

  loadProjectsBasedOnRole();
}, []);


  return (
    <>
      <TitleBar
        id="header-text-add-task"
        title={t("RAL1108")}
        navBack={true}
        onBackClick={handleCancelButton}
      ></TitleBar>
      <div className="addnewtask">
        <Accordion
          className="accordion"
          expanded={expandedAccordion === "basic"}
          // onChange={(_, isExpanded) =>
          //     setExpandedAccordion(isExpanded ? "basic" : null)
          // }
          square={false}
          elevation={0}
          sx={{
            "&.MuiAccordion-root": {
              "::before": {
                content: "none",
              },
            },
          }}
        >
          <AccordionSummary
          // expandIcon={<ExpandMoreIcon />}
          >
            <div className="accordion-text-column">
              <span className="labeladdbasic">{t("RAL1019")}</span>
              <span
                className={
                  mode === "dark"
                    ? "descriptionaddbasicDark"
                    : "descriptionaddbasic"
                }
              >
                {t("RAL1162")}
              </span>
            </div>
          </AccordionSummary>
          <AccordionDetails>
            <div>
              <div className="tasktextfield1">
                <CustomTextField
                  required={true}
                  id="text-create-taskname"
                  name="TaskTile"
                  label={t("RAL1109")}
                  placeholder={t("RAL1110")}
                  error={errors.title}
                  helperText={errors.title}
                  value={title}
                  onChange={(e) => {
                    let inputValue = e.target.value;
                    let chars = Array.from(inputValue);
                    if (chars.length > 255) {
                      inputValue = chars.slice(0, 255).join('');
                    }
                    setTitle(inputValue);
                    if (inputValue.trim() !== "") {
                      setErrors((prevErrors) => ({
                        ...prevErrors,
                        title: "",
                      }));
                    }
                  }}
                  inputProps={{
                    maxLength: 255,
                    title: "",
                  }}
                />
                <div className="task-description-counter">
                  {" "}
                  {title?.length}/255
                </div>
              </div>

              <div className="task-description-wrapper">
                <CustomTextField
                  id="text-task-description"
                  name="Description"
                  label={t("RAL0292")}
                  rows={1}
                  placeholder={t("RAL0293")}
                  error={errors.description}
                  helperText={errors.description}
                  value={state.description}
                  inputRef={textAreaRef}
                  multiline
                  onChange={handleDescriptionChange}
                  inputProps={{
                    maxLength: 2000,
                  }}
                  className="task-description-field"
                />
                <div className="task-description-counter">
                  {" "}
                  {state.description?.length}/2000
                </div>
              </div>

              <div className="addtaskProject">
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <label id="task-text-project">
                    {t("RAL0288")}
                    <span className="starRedColor">*</span>
                  </label>
                </Box>
                <CustomProjectSelect
                  id="dropdown-task-create-project"
                  options={projects || []}
                  error={Boolean(errors.projectId)}
                  placeholder={t("RAL1163")}
                  value={projectId}
                  helperText={errors.projectId || ""}
                  onChange={(newValue) => handleProjectSet(newValue)}
                  handleClear={() => {
                    setProjectId("");
                    setSelectedProject(null);
                    setTimelineStartDate(null);
                    setTimelineEndDate(null);
                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      projectId: "",
                      timelineStartDate: "",
                      timelineEndDate: "",
                    }));
                  }}
                />
                <div className="task-project-timeline">
                  {t("RAL1135")}
                  {projectStartDate &&
                    moment(projectStartDate).format("DD-MM-YYYY")}
                  {projectStartDate &&
                    projectEndDate &&
                    moment(projectEndDate).isValid() &&
                    ` ${t("RAL1002")} ${moment(projectEndDate).format("DD-MM-YYYY")}`}
                </div>
              </div>

              <div className="datepicker-dimension">
                <div className="datepicker-dimension11">
                  <CustomizedDatePicker
                    required={selectedProject != null}
                    id="btn-date-task"
                    name="btn-date-task"
                    label={t("RAL1113")}
                    placeholder={t("RAL1039")}
                    // disabled={selectedProject === null}
                    disabled={!selectedProject}
                    value={timelineStartDate}
                    error={Boolean(errors.timelineStartDate)}
                    helperText={errors.timelineStartDate || ""}
                    onChange={handleTimelineStartDate}
                    minDate={projectStartDate || undefined}
                    maxDate={projectEndDate || undefined}
                  />
                </div>
                <div className="datepicker-dimension11">
                  <CustomizedDatePicker
                    required={
                      projectStartDate &&
                      projectEndDate &&
                      moment(projectEndDate).isValid()
                    }
                    id="btn-date-task"
                    name="btn-date-task"
                    label={t("RAL1114")}
                    placeholder={t("RAL1040")}
                    // disabled={selectedProject === null}
                    isEndDateClear={
                      !(
                        projectStartDate &&
                        projectEndDate &&
                        moment(projectEndDate).isValid()
                      )
                    }
                    disabled={!selectedProject}
                    value={timelineEndDate}
                    onChange={handleTimelineEndDate}
                    error={Boolean(errors.timelineEndDate)}
                    helperText={errors.timelineEndDate || ""}
                    minDate={
                      timelineStartDate || projectStartDate || new Date()
                    }
                    maxDate={projectEndDate || undefined}
                  />
                </div>
              </div>
            </div>
          </AccordionDetails>
        </Accordion>
      </div>
      <Grid
        container
        item
        xs={12}
        lg={12}
        spacing={3}
        alignItems={"end"}
        justifyContent={"flex-end"}
        mt={1}
        mb={2}
      >
        <Grid
          sx={{
            position: "sticky",
            bottom: "1.5rem",
          }}
        >
          <CustomButton
            place="sp-cancel"
            variant="outlined"
            class="memberButton"
            id="btn-cancel-task"
            name="btn-cancel-task"
            onClick={handleCancelButton}
          >
            {t("RAL0177")}
          </CustomButton>
          <CustomButton
            variant={"contained"}
            class="memberButton"
            id="btn-add-task"
            name="btn-add-task"
            onClick={handleCreateTask}
            disabled={isSaveButtonDisable}
          >
            {t("RAL0170")}
          </CustomButton>
        </Grid>
        <CancelDialog
          open={isCancelDialogOpen}
          onClose={handleCloseCancelDialog}
          onConfirm={handleConfirmCancel}
          header={t("RAL0382")}
          message={t("RAL0378")}
        />
      </Grid>
    </>
  );
}
